<template>
    <div class="mt-2">
        <MazBtn size="sm" v-if="permissions && $getPermission(permissions,4).is_granted" @click="shiftSettingsModal = true" class="ml-3 sm:ml-24">
            <font-awesome-icon icon="fa-solid fa-gears" /> {{ $t('Settings') }}
        </MazBtn>
        <span v-else class="ml-3 sm:ml-24"></span>
        <MazBtn v-if="permissions && $getPermission(permissions,5).is_granted" @click="advancedSettings = true" size="sm" class="ml-2">Advanced</MazBtn>

        <MazDialog title="Copy Shifts" v-model="advancedSettings" class="max-w-3xl p-5">
            <div class="max-w-3xl grid grid-cols-2 gap-10">
                <p>This feature allows you to effortlessly copy shift schedules from one week to another, saving time and ensuring consistency. Simply select the source date range and the target start date, and the system will replicate the shifts for the specified company while automatically skipping any existing shifts for the same model, date, and shift number.</p>
                <form @submit.prevent="handleShiftsCopySubmit">
                    <MazPicker
                        v-model="form.source_start_date"
                        size="sm"
                        pickerPosition="right"
                        :label="'Source Start Date'"
                        class="mb-4 w-full"
                    /><br />
                    <MazPicker
                        v-model="form.source_end_date"
                        size="sm"
                        pickerPosition="right"
                        :label="'Source End Date'"
                        class="mb-4 w-full"
                    /><br />
                    <MazPicker
                        v-model="form.target_start_date"
                        size="sm"
                        pickerPosition="right"
                        :label="'Target Start Date'"
                        class="mb-4 w-full"
                    /><br />
                    <MazBtn type="submit" color="primary" :loading="isSubmitting" size="sm">
                        Copy Shifts
                    </MazBtn>
                </form>
            </div>
        </MazDialog>

        <section class="flex absolute right-2 top-12 text-textOnBkg dark:text-dark.textOnBkg">
            <MazSelect v-if="groupList && groupList.length>1"
                class="mr-2 w-36"
                v-model="selectedGroup"
                size="sm"
                :options="groupList"
                :label="$t('group')"
                optionValueKey="id"
                optionLabelKey="name"
                option-input-value-key="name"
            />
            <span v-else class="mr-5 text-lg mt-1 float-left dark:text-dark.panelsText">{{singleGroup}}</span>

            <slot v-if="groupIndex > -1 && groupList && (((groupList.length>0) && (selectedGroup != null)) || ((selectedGroup != null && groupList[groupIndex] && groupList[groupIndex]?.hasChildren) || (singleGroup && groupList[groupIndex] && groupList[groupIndex]?.hasChildren)))">
                <MazSelect v-if="groupIndex > -1 && groupList[groupIndex] && groupList[groupIndex]?.hasChildren && groupList[groupIndex]?.children.length>0"
                    class="mr-1 w-36"
                    size="sm"
                    v-model="selectedTeam"
                    :options="groupList[groupIndex].children"
                    :label="$t('team')"
                    optionValueKey="id"
                    optionLabelKey="name"
                    option-input-value-key="name"
                />
                <span v-else-if="groupIndex > -1 && groupList[groupIndex] && groupList[groupIndex]?.hasChildren" class="text-panelsText dark:text-dark.panelsText float-left mr-5 text-lg mt-1">{{groupList[groupIndex].children[0].name}}</span>
            </slot>

            <div class="p-2" v-if="selectedGroup">
                <font-awesome-icon icon="fas fa-filter-circle-xmark" class="text-decline" @click="selectedGroup = !singleGroup ? null : selectedGroup, selectedTeam = null" />
            </div>

            <MazPicker
                v-model="dRange"
                size="sm"
                pickerPosition="right"
                :label="$t('select_period')"
                double
                :shortcuts="pickerShortcuts"
            />
        </section>
        
        <shiftsTable :selectedModel="selectedModel" :myself="myself" :permissions="permissions" :selectedTeam="selectedTeam" :selectedGroup="selectedGroup" :dateRange="[dateFrom, dateTo]"/>
    </div>
    <div v-if="permissions != null && (!$getPermission(permissions,4).is_granted)" class="text-center mt-52 text-textOnBkg">{{ $t("no_permission") }}</div>

    <transition v-if="permissions && $getPermission(permissions,5).is_granted" name="zoom-fade">
        <section v-if="shiftSettingsModal" @click="shiftSettingsModal = false" class="backdrop-blur w-full h-full fixed inset-0 bg-gray-700 bg-opacity-30 z-50 flex justify-center items-center px-4 text-black">
            <section @click.stop class="bg-bkg dark:bg-dark.bkg text-textOnBkg dark:text-dark.textOnBkg rounded-lg w-full max-h-screen max-w-md sm:max-w-5xl mx-auto my-auto p-8 sm:p-12 z-50 overflow-auto relative">
                <font-awesome-icon @click="shiftSettingsModal = false" icon="fas fa-xmark" class="transition absolute p-6 top-0 right-0 text-xl cursor-pointer hover:opacity-50" />
                <shifts-settings :noarrow="true" @close="handleClose" />
            </section>
        </section>
    </transition>
</template>

<script setup>
    import shiftsTable from "@/components/shifts/shiftsTable.vue"
    import shiftsSettings from "@/components/settings/shifts.vue"
</script>

<script>
    import { useAuthStore } from '@/stores/auth';
    import { get_groups, copy_shifts } from "@/api/api.js"; 
    export default {
        components() {
            shiftsTable,
            shiftsSettings
        },
        data() {
            return {
                advancedSettings: false,
                form: {
                    source_start_date: '',
                    source_end_date: '',
                    target_start_date: '',
                },
                isSubmitting: false,
                error: null,
                groupList: [],
                modelsList: [],
                unselectGroups: false,
                shiftSettingsModal:false,
                selectedPlatform: null,
                selectedEmpl: null,
                selectedTeam: null,
                selectedModelN: null,
                selectedModel: null,
                selectedGroup: null,
                dateFrom: this.$moment().subtract(1, 'days').format('YYYY-MM-DD'),
                dateTo: this.$moment().add(4, 'days').format('YYYY-MM-DD'),
                groupList: [],
                dRange: {start: null, end: null},
                pickerShortcuts: [
                    { label: this.$t('this_week'), value: {start:this.$moment().startOf('week').toDate(), end: this.$moment().endOf('week').toDate()} },
                    { 
                        label: this.$t('next_week'), 
                        value: {
                            start: this.$moment().startOf('isoWeek').add(1, 'weeks').subtract(1, 'days').toDate(),
                            end: this.$moment().startOf('isoWeek').add(1, 'weeks').add(6, 'days').toDate()
                        } 
                    },
                    { label: this.$t('last_7_days'), value: {start: this.$moment().subtract(7, 'days').toDate(), end: this.$moment().toDate()} },
                    { label: this.$t('last_30_days'), value: {start: this.$moment().subtract(30, 'days').toDate(), end: this.$moment().toDate()} },
                    { label: this.$t('next_7_days'), value: {start: this.$moment().toDate(), end: this.$moment().add(7, 'days').toDate()} },
                    { label: this.$t('next_30_days'), value: {start: this.$moment().toDate(), end: this.$moment().add(30, 'days').toDate()} },
                ],
                singleGroup: null
            }
        },
        computed: {
            authStore() {
                return useAuthStore();
            },
            myself() {
                return this.authStore.myself;
            },
            permissions() {
                return this.authStore.permissions;
            },
            groupIndex() {
                return this.groupList ? this.groupList.findIndex(b => b.id === this.selectedGroup) : null;
            }
        },
        watch: {
            dRange: function (n,o) {
                this.dateFrom = n.start ? n.start : this.dateFrom;
                this.dateTo = n.end ? n.end : this.dateTo;
            },
        },
        methods: {
            async handleShiftsCopySubmit() {
                // Basic validation
                if (!this.form.source_start_date || !this.form.source_end_date || !this.form.target_start_date) {
                    this.error = 'All date fields are required.';
                    return;
                }
                if (new Date(this.form.source_end_date) < new Date(this.form.source_start_date)) {
                    this.error = 'Source End Date must be after Source Start Date.';
                    return;
                }
                
                this.isSubmitting = true;
                this.error = null;
                
                try {
                    await copy_shifts(this.$cookies.get("userToken"), this.$cookies.get('c_id'), this.form.source_start_date, this.form.source_end_date, this.form.target_start_date);
                    alert('Shifts copied successfully!');
                } catch (error) {
                    this.error = error.response?.data?.message || 'An error occurred while copying shifts.';
                } finally {
                    this.isSubmitting = false;
                }
            },
            updateSelectedTeam(team) {
                this.selectedTeam = team;
            },
            updateSelectedPlatform(platform) {
                this.selectedPlatform = platform;
            },
            updateSelectedEmpl(empl) {
                this.selectedEmpl = empl;
            },
            updateSelectedModelN(modelN) {
                this.selectedModelN = modelN;
            },
            updateSelectedModel(model) {
                this.selectedModel = model;
            },
            updateSelectedGroupN(groupN) {
                this.selectedGroupN = groupN;
            },
            updateSelectedGroup(group) {
                this.selectedGroup = group;
            },
            updateDateRange(dates) {
                this.dateFrom = dates.start ? dates.start : this.dateFrom;
                this.dateTo = dates.end ? dates.end : this.dateTo;
            },
            handleClose() {
                this.shiftSettingsModal = false;
            }
        },
        async mounted() {
            this.groupList = await get_groups(this.$cookies.get("userToken"),this.$cookies.get('c_id'));
            if (this.groupList && this.groupList.length == 1) {
                this.singleGroup = this.groupList[0].name;
                this.selectedGroup = this.groupList[0].id;
            }
        }
    }
</script>